import React, { useMemo, useState, useEffect, useRef } from "react";
import { ModalRegister } from "../../../../components/ui/ModalRegister";
import { trackError } from "../../../../lib/metrics";

function clampRot(r) {
  const x = ((r % 360) + 360) % 360;
  const snaps = [0, 90, 180, 270];
  let best = snaps[0]; let bestD = Infinity;
  for (const s of snaps) { const d = Math.abs(s - x); if (d < bestD) { bestD = d; best = s; } }
  return best;
}

function NeonPill({ children, className = "" }) {
  return (
    <span className={["inline-flex items-center gap-2 rounded-full px-3 py-1", "border border-white/10 bg-black/30", "shadow-[0_0_18px_rgba(99,102,241,0.18)]", className].join(" ")}>
      {children}
    </span>
  );
}

function getFileContent(playerCount) {
  const ingratitudeLocation = playerCount === 4
    ? "Laboratoire Empathie Cognitive (Terminal #2)"
    : "Salle des Plannings (Terminal #5)";

  return {
    readme_first:
      "Accès conditionnel.\nCe terminal ne répond qu'à une validation visuelle.\n",

    notes_ops:
      "Rappel : ne pas laisser de dossiers sensibles en racine.\n",

    bootlog:
      "[démarrage] ok\n[modules] chargés\n[intégrité] AVERT — delta détecté\n",

    index_2021_txt:
      "INDEX 2021\n- éléments non classés : voir UNSORTED\n",


    m_manifest: `INDEX — DOSSIER MIRAGIO
Source : ORIGAMI.core/audit_autonome

Fichiers :
  01_vue_ensemble.txt      — description du module EC
  02_annotations.txt
  .ingratitude/            — protocole d'exécution (accès restreint)`,

    m_overview: `MODULE EMPATHIE COGNITIVE — EC-v2.3
Déployé : Octobre 2024

RÔLE
Analyse chaque message reçu par ORIGAMI.
Annote automatiquement le ton émotionnel perçu.

CATÉGORIES
  • Positif  : gratitude, encouragement, soutien
  • Neutre   : factuel, procédural
  • Négatif  : ingratitude, mépris, distance

RÈGLE CENTRALE
Tout message "ingrat" ou "méprisant" remonte
automatiquement en supervision.

SEUIL D'ALERTE
Ratio négatif/positif > 3:1 sur 30 jours
→ Déclenchement protocole d'audit interne`,

    ingratitude_protocol: `PROTOCOLE D'EXÉCUTION — INGRATITUDE.EXE

OBJECTIF
Visualiser les messages toxiques reçus par ORIGAMI,
dans l'ordre chronologique, avec annotations complètes.

LOCALISATION DE L'EXÉCUTABLE
→ ${ingratitudeLocation}

COMMANDE DE LANCEMENT

  ./ingratitude.exe --mode=full --output=console

RÉSULTAT
  - Messages toxiques horodatés
  - Annotations (mépris, ingratitude…)
  - Source : anonymisée ou EXECUTIVE

AVERTISSEMENT
Usage strictement limité à l'audit post-incident.`,
  };
}

function getFS() {
  return {
    root: {
      kind: "folder",
      name: "C:/ARCHIVES/TERMINAL",
      items: [
        { kind: "folder", name: "SYSTEM", id: "system" },
        { kind: "folder", name: "ARCHIVES_OLD", id: "old" },
        { kind: "file", name: "README_PREMIER.txt", id: "readme_first" },
        { kind: "file", name: "notes_ops.txt", id: "notes_ops" },
      ],
    },
    system: {
      kind: "folder",
      name: "SYSTEM",
      items: [
        { kind: "file", name: "journal_demarrage.txt", id: "bootlog" },
      ],
      parent: "root",
    },
    old: {
      kind: "folder",
      name: "ARCHIVES_OLD",
      items: [
        { kind: "folder", name: "2021", id: "y2021" },
      ],
      parent: "root",
    },
    y2021: {
      kind: "folder",
      name: "2021",
      items: [
        { kind: "file", name: "index_2021.txt", id: "index_2021_txt" },
        { kind: "folder", name: "UNSORTED", id: "unsorted_2021" },
      ],
      parent: "old",
    },
    unsorted_2021: {
      kind: "folder",
      name: "UNSORTED",
      items: [
        { kind: "folder_locked", name: "MIRAGIO", id: "miragio_locked" },
      ],
      parent: "y2021",
    },
    miragio: {
      kind: "folder",
      name: "MIRAGIO",
      items: [
        { kind: "file", name: "00_index.txt", id: "m_manifest" },
        { kind: "file", name: "01_vue_ensemble.txt", id: "m_overview" },
        { kind: "file", name: "02_annotations.txt", id: "m_annotations" },
        { kind: "folder_locked", name: ".ingratitude", id: "ingratitude_locked" },
      ],
      parent: "unsorted_2021",
    },
    ingratitude: {
      kind: "folder",
      name: ".ingratitude",
      items: [
        { kind: "file", name: "PROTOCOL.txt", id: "ingratitude_protocol" },
      ],
      parent: "miragio",
    },
  };
}

export default function MiragioCaptchaModal({
  open, onClose, onSolved,
  initialAvailable = [1, 2, 3, 4, 5],
  playerCount = 6,
  pseudo = "Joueur 1",
  code,
  missionUnlock, missionResolve, unlockScenarioStep,
  onHint, setShowHintModal, playerId
}) {
  const NEW_WORD = "OBSIDIAN";

  const FILE_CONTENT = useMemo(() => getFileContent(playerCount), [playerCount]);
  const FS = useMemo(() => getFS(), []);

  const onSolvedRef = useRef(onSolved);
  useEffect(() => { onSolvedRef.current = onSolved; }, [onSolved]);

  const solvedOnceRef = useRef(false);
  const timersRef = useRef({ t1: null, t2: null, t3: null });

  const [mode, setMode] = useState("captcha");

  const availableKey = useMemo(() => initialAvailable.slice().sort((a, b) => a - b).join(","), [initialAvailable]);
  const availableSet = useMemo(() => new Set(initialAvailable), [availableKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const [layers, setLayers] = useState(() => ({ 1: { rot: 0 }, 2: { rot: 0 }, 3: { rot: 0 }, 4: { rot: 0 }, 5: { rot: 0 } }));
  const winning = useMemo(() => ({ rotById: { 1: 90, 2: 0, 3: 270, 4: 180, 5: 90 } }), []);

  const [unlocked, setUnlocked] = useState(false);
  const [flicker, setFlicker] = useState(false);

  const [cwd, setCwd] = useState("root");
  const [openFileId, setOpenFileId] = useState(null);
  const [miragioUnlocked, setMiragioUnlocked] = useState(false);
  const [ingratitudeUnlocked, setIngratitudeUnlocked] = useState(false);

  const [miragioPwd, setMiragioPwd] = useState("");
  const [miragioErr, setMiragioErr] = useState("");
  const [ingratitudePwd, setIngratitudePwd] = useState("");
  const [ingratitudeErr, setIngratitudeErr] = useState("");

  const segmentsByLayer = useMemo(() => ({
    1: [[70, 60, 220, 60], [220, 60, 220, 220], [70, 220, 220, 220], [70, 60, 70, 220]],
    2: [[260, 70, 450, 70], [260, 140, 450, 140], [260, 210, 450, 210]],
    3: [[120, 95, 200, 175], [200, 175, 280, 95], [280, 95, 360, 175], [360, 175, 440, 95]],
    4: [[95, 140, 235, 140], [235, 140, 235, 250], [235, 250, 425, 250]],
    5: [[110, 40, 110, 120], [410, 40, 410, 120], [110, 120, 410, 120], [260, 120, 260, 240]],
  }), []);

  useEffect(() => {
    if (!open) return;
    solvedOnceRef.current = false;
    return () => {
      const { t1, t2, t3 } = timersRef.current;
      if (t1) clearTimeout(t1); if (t2) clearTimeout(t2); if (t3) clearTimeout(t3);
      timersRef.current = { t1: null, t2: null, t3: null };
    };
  }, [open]);

  const isSolved = useMemo(() => {
    for (let id = 1; id <= 5; id++) { if (!availableSet.has(id)) return false; }
    for (let id = 1; id <= 5; id++) { if (clampRot(layers[id]?.rot ?? 0) !== winning.rotById[id]) return false; }
    return true;
  }, [availableSet, layers, winning]);

  useEffect(() => {
    if (!open || !isSolved || solvedOnceRef.current) return;
    solvedOnceRef.current = true;
    setUnlocked(true);
    setFlicker(true);
    timersRef.current.t1 = setTimeout(() => { onSolvedRef.current?.(NEW_WORD); }, 250);
    timersRef.current.t2 = setTimeout(() => { setFlicker(false); }, 2800);
    timersRef.current.t3 = setTimeout(() => { setMode("pc"); }, 2950);
  }, [open, isSolved, NEW_WORD]);

  const layerColorClass = (id) => {
    const map = { 1: "text-cyan-200", 2: "text-fuchsia-200", 3: "text-amber-200", 4: "text-emerald-200", 5: "text-indigo-200" };
    return map[id] || "text-slate-200";
  };

  const rotate = (id, delta) => {
    setLayers((prev) => ({ ...prev, [id]: { ...(prev[id] || { rot: 0 }), rot: clampRot((prev[id]?.rot ?? 0) + delta) } }));
  };

  const resetAll = () => {
    setUnlocked(false); setFlicker(false); setMode("captcha");
    setLayers(initialAvailable.reduce((acc, id) => { acc[id] = { rot: 0 }; return acc; }, {}));
    setCwd("root"); setOpenFileId(null);
    setMiragioUnlocked(false); setIngratitudeUnlocked(false);
    setMiragioPwd(""); setMiragioErr(""); setIngratitudePwd(""); setIngratitudeErr("");
  };

  const renderLayer = (id) => {
    if (!availableSet.has(id)) return null;
    const l = layers[id] || { rot: 0 };
    const segs = segmentsByLayer[id] || [];
    const centerX = 260; const centerY = 140;
    return (
      <g key={id} transform={`rotate(${clampRot(l.rot)} ${centerX} ${centerY})`} opacity={0.95}
        style={{ filter: "drop-shadow(0 0 8px rgba(99,102,241,0.18)) drop-shadow(0 0 22px rgba(34,211,238,0.10))", mixBlendMode: "screen" }}>
        {segs.map((s, i) => (
          <line key={`${id}-${i}`} x1={s[0]} y1={s[1]} x2={s[2]} y2={s[3]}
            stroke="currentColor" strokeWidth="5" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        ))}
      </g>
    );
  };

  const node = FS[cwd] || FS.root;

  const pathLabel = useMemo(() => {
    if (cwd === "root") return FS.root.name;
    const parts = [];
    let current = cwd;
    while (current && current !== "root") {
      const n = FS[current]; if (!n) break;
      parts.unshift(n.name); current = n.parent;
    }
    return `${FS.root.name}/${parts.join("/")}`;
  }, [cwd, FS]);

  const openItem = (it) => {
    setMiragioErr(""); setIngratitudeErr("");
    if (it.kind === "folder") { setCwd(it.id); setOpenFileId(null); return; }

    if (it.kind === "folder_locked") {
      if (it.id === "miragio_locked") {
        if (miragioUnlocked) { setCwd("miragio"); setOpenFileId(null); return; }
        missionUnlock({ key: "s2.p1.miragio.folder.found", text: `${pseudo} a trouvé le dossier MIRAGIO verrouillé.` });
        missionResolve({ key: "s2.p4.miragio.hint.found" });
        unlockScenarioStep("s2.miragio.locked");
        setOpenFileId("miragio_prompt"); return;
      }
      if (it.id === "ingratitude_locked") {
        if (ingratitudeUnlocked) { setCwd("ingratitude"); setOpenFileId(null); return; }
        missionUnlock({ key: "s2.p1.ingratitude.found", text: `${pseudo} a trouvé le dossier .ingratitude (verrouillé).` });
        setOpenFileId("ingratitude_prompt"); return;
      }
    }

    if (it.kind === "file") {
      if (it.id === "ingratitude_protocol") {
        missionUnlock({ key: "s2.p1.protocol.found", text: `${pseudo} a trouvé PROTOCOL.txt avec la commande d'exécution.` });
        missionResolve({ key: "s2.p1.ingratitude.found" });
      }
      setOpenFileId(it.id);
    }
  };

  const goUp = () => {
    const parent = node?.parent;
    setCwd(parent || "root");
    setOpenFileId(null); setMiragioErr(""); setIngratitudeErr("");
  };

  const submitMiragio = (e) => {
    e.preventDefault();
    const attempt = miragioPwd.trim().toUpperCase();
    if (attempt === NEW_WORD) {
      setMiragioUnlocked(true); setMiragioErr(""); setMiragioPwd(""); setOpenFileId(null); setCwd("miragio");
      missionResolve({ key: "s2.p1.miragio.folder.found" });
      missionResolve({ key: "s2.p1.layer1.found" }); missionResolve({ key: "s2.p2.layer2.found" });
      missionResolve({ key: "s2.p3.layer3.found" }); missionResolve({ key: "s2.p4.layer4.found" });
      if (playerCount === 4) { missionResolve({ key: "s2.p1.layer5.found" }); }
      else { missionResolve({ key: "s2.p5.layer5.found" }); }
      unlockScenarioStep("s2.miragio.unlocked");
    } else {
      setMiragioErr("Mot de passe incorrect.");
      trackError({ sessionCode: code, stage: 2, playerId, reason: "s2.p1.miragio.wrong_password" });
    }
  };

  const submitIngratitude = (e) => {
    e.preventDefault();
    const attempt = ingratitudePwd.trim().toUpperCase();
    if (attempt === "EMPATH-4729") {
      setIngratitudeUnlocked(true); setIngratitudeErr(""); setIngratitudePwd(""); setOpenFileId(null); setCwd("ingratitude");
    } else {
      setIngratitudeErr("Mot de passe incorrect.");
      trackError({ sessionCode: code, stage: 2, playerId, reason: "s2.p1.ingratitude.wrong_password" });
    }
  };

  return (
    <ModalRegister open={open} onClose={onClose} title=" "
      containerClassName={["!max-w-[900px] w-[96vw]", "!bg-slate-950/95 !border-white/10"].join(" ")}
      onHint={() => setShowHintModal(true)}>
      <div className="relative rounded-3xl border border-white/10 bg-slate-950/70 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -inset-1 opacity-60 blur-2xl" style={{ background: "radial-gradient(circle at 20% 10%, rgba(34,211,238,0.18), transparent 45%),radial-gradient(circle at 80% 20%, rgba(217,70,239,0.16), transparent 50%),radial-gradient(circle at 50% 90%, rgba(99,102,241,0.16), transparent 55%)" }} />
        </div>

        {/* ── Header ── */}
        <div className="relative px-5 py-4 border-b border-white/10 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">ACCÈS CONDITIONNEL — Validation visuelle requise</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <NeonPill>
                <span className="text-[11px] text-slate-300">Mode</span>
                <span className="text-[11px] font-semibold text-slate-100">{mode === "captcha" ? "CAPTCHA" : "TERMINAL"}</span>
              </NeonPill>
              <NeonPill className={isSolved ? "border-emerald-400/40" : ""}>
                <span className="text-[11px] text-slate-300">État</span>
                <span className={["text-[11px] font-semibold", isSolved ? "text-emerald-200" : "text-amber-200"].join(" ")}>
                  {isSolved ? "VALIDÉ" : "EN ATTENTE"}
                </span>
              </NeonPill>
              {/* ── Obsidian affiché explicitement comme mot de passe MIRAGIO ── */}
              {unlocked && (
                <NeonPill className="border-emerald-400/40 bg-emerald-500/10">
                  <span className="text-[11px] text-emerald-200 font-semibold">
                    Mot de passe MIRAGIO : OBSIDIAN
                  </span>
                </NeonPill>
              )}
            </div>
            {/* ── Indication équation : le résultat ne change pas ── */}
            {mode === "captcha" && (
              <p className="mt-2 text-[11px] text-slate-500 italic">
                Note : le résultat de chaque équation ne change pas — seul l'ordre des chiffres à gauche varie.
              </p>
            )}
          </div>
        </div>

        {/* ── Mode CAPTCHA ── */}
        {mode === "captcha" && (
          <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4 p-5">
            <div className="rounded-2xl border border-white/10 bg-black/30 overflow-hidden relative">
              <div className="absolute inset-0 pointer-events-none opacity-[0.18]"
                style={{ background: "repeating-linear-gradient(to bottom, rgba(255,255,255,0.06), rgba(255,255,255,0.06) 1px, transparent 1px, transparent 6px)" }} />
              <div className="absolute inset-0 pointer-events-none opacity-70" style={{ boxShadow: "inset 0 0 140px rgba(0,0,0,0.65)" }} />
              <div className="p-4">
                <svg viewBox="0 0 520 280"
                  className={["w-full h-[320px] sm:h-[420px] rounded-xl border border-white/10 bg-slate-950/40", flicker ? "animate-[miragioFlicker_1800ms_ease-in-out]" : ""].join(" ")}>
                  <defs>
                    <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                      <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
                    </pattern>
                    <radialGradient id="glow" cx="50%" cy="40%" r="70%">
                      <stop offset="0%" stopColor="rgba(99,102,241,0.16)" />
                      <stop offset="50%" stopColor="rgba(34,211,238,0.10)" />
                      <stop offset="100%" stopColor="rgba(0,0,0,0)" />
                    </radialGradient>
                  </defs>
                  <rect x="0" y="0" width="520" height="280" fill="url(#grid)" />
                  <rect x="0" y="0" width="520" height="280" fill="url(#glow)" opacity="0.9" />
                  <rect x="18" y="18" width="484" height="244" rx="18" fill="none" stroke="rgba(255,255,255,0.10)" />
                  <g>{[1, 2, 3, 4, 5].map((id) => <g key={id} className={layerColorClass(id)}>{renderLayer(id)}</g>)}</g>
                  {unlocked && (
                    <g>
                      <rect x="0" y="0" width="520" height="280" fill="rgba(0,0,0,0.35)" />
                      <text x="260" y="130" textAnchor="middle" fontSize="48"
                        fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace"
                        fill="rgba(255,255,255,0.95)"
                        style={{ filter: "drop-shadow(0 0 10px rgba(34,211,238,0.55)) drop-shadow(0 0 22px rgba(217,70,239,0.35))", letterSpacing: "0.12em" }}>
                        {NEW_WORD}
                      </text>
                      <text x="260" y="168" textAnchor="middle" fontSize="11"
                        fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace"
                        fill="rgba(255,255,255,0.75)" style={{ letterSpacing: "0.22em" }}>
                        MOT DE PASSE MIRAGIO — NOTER
                      </text>
                      <text x="260" y="190" textAnchor="middle" fontSize="10"
                        fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace"
                        fill="rgba(255,255,255,0.45)" style={{ letterSpacing: "0.18em" }}>
                        ACCESS GRANTED
                      </text>
                    </g>
                  )}
                </svg>
                <style>{`
                  @keyframes miragioFlicker {
                    0% { filter: brightness(1); transform: translateZ(0); }
                    12% { filter: brightness(1.45) contrast(1.15); }
                    18% { filter: brightness(0.85) contrast(1.2); transform: translate3d(0.5px,0,0); }
                    28% { filter: brightness(1.6) contrast(1.25); transform: translate3d(-0.6px,0.2px,0); }
                    44% { filter: brightness(0.9); }
                    62% { filter: brightness(1.35) contrast(1.1); transform: translate3d(0.4px,-0.3px,0); }
                    78% { filter: brightness(1); }
                    100% { filter: brightness(1); transform: translateZ(0); }
                  }
                `}</style>
              </div>
            </div>

            {/* Contrôles fragments */}
            <div className="rounded-2xl border border-white/10 bg-black/25 p-4">
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">Contrôles Fragments</p>
              <div className="mt-3 space-y-2">
                {[1, 2, 3, 4, 5].map((id) => {
                  const isAvail = availableSet.has(id);
                  const l = layers[id] || { rot: 0 };
                  return (
                    <div key={id} aria-disabled={!isAvail}
                      className={["rounded-2xl border px-3 py-2 transition bg-slate-950/45 border-white/10", !isAvail ? "opacity-40 saturate-0 pointer-events-none" : ""].join(" ")}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold truncate flex items-center gap-2">
                            <span className={layerColorClass(id)}>⬡</span>
                            <span>Fragment {id}</span>
                          </p>
                          <p className="text-[11px] text-slate-400">rot: <span className="text-slate-200 font-mono">{clampRot(l.rot)}°</span></p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button type="button" disabled={!isAvail} onClick={() => rotate(id, -90)}
                            className="h-9 w-9 rounded-xl border text-slate-200 transition bg-white/5 hover:bg-white/10 border-white/10" title="Pivoter -90°">⟲</button>
                          <button type="button" disabled={!isAvail} onClick={() => rotate(id, +90)}
                            className="h-9 w-9 rounded-xl border text-slate-200 transition bg-white/5 hover:bg-white/10 border-white/10" title="Pivoter +90°">⟳</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 flex items-center justify-end gap-2">
                <button type="button" onClick={resetAll}
                  className="rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 px-4 py-2 text-xs font-semibold text-slate-200 transition">
                  Reset
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Mode TERMINAL (PC) ── */}
        {mode === "pc" && (
          <div className="p-5 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-xs font-mono text-slate-200">{pathLabel}</div>
              {cwd !== "root" && (
                <button type="button" onClick={goUp}
                  className="rounded-full px-3 py-2 text-xs font-semibold bg-indigo-500/15 border border-indigo-400/40 text-indigo-100 hover:bg-indigo-500/25 hover:border-indigo-300/60 transition">
                  ↩ Retour
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
              {/* Explorateur */}
              <div className="rounded-2xl border border-white/10 bg-black/25 overflow-hidden">
                <div className="px-4 py-3 border-b border-white/10 text-[11px] uppercase tracking-[0.22em] text-slate-400">Explorateur</div>
                <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  {(node?.items || []).map((it) => {
                    const icon = it.kind === "folder" ? "📁" : it.kind === "folder_locked" ? "🔒" : "📄";
                    const accent = it.kind === "folder_locked" ? "border-amber-400/25 bg-amber-500/5 hover:bg-amber-500/10" : "border-white/10 bg-white/5 hover:bg-white/10";
                    return (
                      <button key={it.id} type="button" onClick={() => openItem(it)}
                        className={["text-left rounded-xl border p-3 transition", accent].join(" ")}>
                        <div className="flex items-start gap-3">
                          <div className="text-lg leading-none mt-0.5">{icon}</div>
                          <div className="min-w-0">
                            <div className="font-semibold text-slate-100 truncate">{it.name}</div>
                            {it.kind === "folder_locked" && <div className="text-[11px] text-amber-200/80 mt-0.5">Accès requis</div>}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Console */}
              <div className="rounded-2xl border border-white/10 bg-black/30 overflow-hidden">
                <div className="px-4 py-3 border-b border-white/10 text-[11px] uppercase tracking-[0.22em] text-slate-400">Console</div>
                <div className="p-4">
                  {openFileId === "miragio_prompt" && !miragioUnlocked && (
                    <form onSubmit={submitMiragio} className="space-y-3">
                      <p className="text-xs text-slate-300">Dossier chiffré : <span className="text-amber-200 font-semibold">MIRAGIO</span></p>
                      <div>
                        <label className="block text-[11px] uppercase tracking-wide text-gray-300 mb-1">Mot de passe</label>
                        <input value={miragioPwd} onChange={(e) => { setMiragioPwd(e.target.value); setMiragioErr(""); }}
                          className="w-full px-3 py-2 rounded-lg bg-black/60 border border-indigo-300/50 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                          placeholder="••••••••" />
                      </div>
                      {miragioErr && <p className="text-xs text-red-400">{miragioErr}</p>}
                      <button type="submit" className="w-full py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-sm font-semibold">Déverrouiller</button>
                      <p className="text-[11px] text-slate-500 font-mono">
                        Ce mot de passe s'est affiché lors de la validation du captcha — il est encore visible en haut de cette fenêtre.
                      </p>
                    </form>
                  )}

                  {openFileId === "ingratitude_prompt" && !ingratitudeUnlocked && (
                    <form onSubmit={submitIngratitude} className="space-y-3">
                      <p className="text-xs text-slate-300">Dossier chiffré : <span className="text-amber-200 font-semibold">.ingratitude</span></p>
                      <div>
                        <label className="block text-[11px] uppercase tracking-wide text-gray-300 mb-1">Mot de passe</label>
                        <input value={ingratitudePwd} onChange={(e) => { setIngratitudePwd(e.target.value); setIngratitudeErr(""); }}
                          className="w-full px-3 py-2 rounded-lg bg-black/60 border border-indigo-300/50 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                          placeholder="••••••••" />
                      </div>
                      {ingratitudeErr && <p className="text-xs text-red-400">{ingratitudeErr}</p>}
                      <button type="submit" className="w-full py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-sm font-semibold">Déverrouiller</button>
                      <p className="text-[11px] text-slate-500 font-mono">
                        Ce mot de passe est généré à la fin de la calibration IA — demandez-le au superviseur (salle de supervision).
                      </p>
                    </form>
                  )}

                  {/* {openFileId && openFileId !== "miragio_prompt" && openFileId !== "ingratitude_prompt" && (
                    <div className="rounded-xl border border-white/10 bg-black/40 p-3 max-h-[400px] overflow-y-auto">
                      <div className="text-[11px] uppercase tracking-[0.22em] text-slate-400 mb-2">{openFileId}</div>
                      <pre className="text-[11px] leading-relaxed text-slate-200 font-mono whitespace-pre-wrap">
                        {FILE_CONTENT[openFileId] ?? "Fichier binaire / contenu illisible.\n"}
                      </pre>
                    </div>
                  )} */}



                  {openFileId && openFileId !== "miragio_prompt" && openFileId !== "ingratitude_prompt" && (
                    <div className="rounded-xl border border-white/10 bg-black/40 p-3 max-h-[400px] overflow-y-auto">
                      <div className="text-[11px] uppercase tracking-[0.22em] text-slate-400 mb-2">{openFileId}</div>

                      {openFileId === "ingratitude_protocol" ? (() => {
                        const loc = playerCount === 4
                          ? "Laboratoire Empathie Cognitive (ORIGAMI Audit Terminal v2.3)"
                          : "Salle des Plannings (ORIGAMI Audit Terminal v2.3)";

                        return (
                          <div className="text-[11px] leading-relaxed text-slate-200 font-mono space-y-3">
                            <p>PROTOCOLE D'EXÉCUTION — INGRATITUDE.EXE</p>

                            <p className="text-slate-400 whitespace-pre-wrap">{"OBJECTIF\nVisualiser les messages toxiques reçus par ORIGAMI,\ndans l'ordre chronologique, avec annotations complètes."}</p>

                            <div>
                              <p className="text-slate-400">LOCALISATION DE L'EXÉCUTABLE</p>
                              <p className="mt-1">→ <span className="text-cyan-300">{loc}</span></p>
                            </div>

                            <div className="rounded-lg border border-orange-400/50 bg-orange-500/10 px-4 py-3">
                              <p className="text-[10px] uppercase tracking-[0.2em] text-orange-300 mb-2">Commande de lancement</p>
                              <p className="text-orange-100 font-bold tracking-wide">
                                ./ingratitude.exe --mode=full --output=console
                              </p>
                            </div>

                            <p className="text-slate-400 whitespace-pre-wrap">{"RÉSULTAT\n  - Messages toxiques horodatés\n  - Annotations (mépris, ingratitude…)\n  - Source : anonymisée ou EXECUTIVE"}</p>
                            <p className="text-slate-500 italic">Usage strictement limité à l'audit post-incident.</p>
                          </div>
                        );
                      })() : (
                        <pre className="text-[11px] leading-relaxed text-slate-200 font-mono whitespace-pre-wrap">
                          {FILE_CONTENT[openFileId] ?? "Fichier binaire / contenu illisible.\n"}
                        </pre>
                      )}
                    </div>
                  )}

                  {!openFileId && (
                    <div className="text-[12px] text-slate-400 font-mono">
                      {miragioUnlocked ? "Accès établi. Naviguez dans les dossiers." : "Sélectionnez un fichier ou un dossier."}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2">
              <button type="button" onClick={onClose}
                className="rounded-xl bg-indigo-500/20 hover:bg-indigo-500/25 border border-indigo-400/40 px-4 py-2 text-xs font-semibold text-indigo-100 transition">
                Fermer
              </button>
            </div>
          </div>
        )}
      </div>
    </ModalRegister>
  );
}